/**
 * Top-level error boundary — prevents the white-screen-of-death by catching
 * any render/runtime error in the tree and showing it (with stack) instead of
 * blanking. Inline-styled so it works even if app CSS is the failure source.
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surfaced on screen below; also log for completeness.
    console.error('[ErrorBoundary]', error, info);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    const box: React.CSSProperties = {
      maxWidth: 640,
      margin: '0 auto',
      padding: '2rem 1.25rem',
      fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
      color: '#2D2D2D',
    };
    const pre: React.CSSProperties = {
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      fontSize: '11px',
      lineHeight: 1.45,
      background: '#F4F1EC',
      border: '1px solid #ECE7EF',
      borderRadius: 10,
      padding: '12px',
      marginTop: '12px',
      maxHeight: '40vh',
      overflow: 'auto',
    };

    return (
      <div style={box}>
        <h1 style={{ fontSize: '1.25rem', color: '#652E87', margin: 0 }}>
          Something went wrong
        </h1>
        <p style={{ color: '#6B7280', marginTop: 8 }}>
          The app hit an error and couldn&rsquo;t continue. Details below (please
          screenshot for support):
        </p>
        <p style={{ fontWeight: 600, marginTop: 12 }}>{error.message}</p>
        {error.stack && <pre style={pre}>{error.stack}</pre>}
        {componentStack && <pre style={pre}>{componentStack}</pre>}
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: 16,
            minHeight: 44,
            padding: '0.5rem 1.5rem',
            background: '#7F3F96',
            color: '#fff',
            border: 'none',
            borderRadius: 10,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}
